/**
 * Serializes PlateJS editor pages into GitHub-flavoured markdown.
 *
 * Node shapes mirror the HTML serializer in ./exportPlate.ts. Pages expose their
 * node array via `page.plateValue`. Used to refresh `policy_markdown_url` whenever
 * the branded editor saves, so the generation page (a markdown renderer) reflects
 * the latest edits instead of raw editor JSON.
 */

type PlateNode = {
  type?: string;
  children?: PlateNode[];
  text?: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  strikethrough?: boolean;
  url?: string;
  alt?: string;
  listStyleType?: string;
};

type PageLike = { plateValue?: unknown };

const isOrdered = (style?: string): boolean =>
  style === 'decimal' || style === 'ordered' || style === 'ol';

const serializeLeaf = (node: PlateNode): string => {
  let t = node.text ?? '';
  if (!t) return '';
  if (node.bold) t = `**${t}**`;
  if (node.italic) t = `*${t}*`;
  if (node.strikethrough) t = `~~${t}~~`;
  if (node.underline) t = `<u>${t}</u>`;
  return t;
};

const inlineText = (children: PlateNode[] | undefined): string =>
  (children ?? [])
    .map((c) => (typeof c.text === 'string' ? serializeLeaf(c) : inlineText(c.children)))
    .join('');

const liText = (li: PlateNode): string => {
  const lic = (li.children ?? []).find((c) => c.type === 'lic');
  return inlineText(lic?.children ?? li.children).replace(/\n/g, ' ');
};

const serializeTable = (table: PlateNode): string => {
  const rows = (table.children ?? []).filter((r) => r.type === 'tr');
  if (rows.length === 0) return '';
  const cellText = (cell: PlateNode) =>
    inlineText(cell.children).replace(/\n/g, ' ').replace(/\|/g, '\\|').trim();
  const toRow = (r: PlateNode) => `| ${(r.children ?? []).map(cellText).join(' | ')} |`;
  const header = rows[0];
  const colCount = (header.children ?? []).length || 1;
  const divider = `| ${Array.from({ length: colCount }, () => '---').join(' | ')} |`;
  const body = rows.slice(1).map(toRow);
  return [toRow(header), divider, ...body].join('\n');
};

// Within a paragraph, the editor stores visible line breaks as embedded "\n"
// in the leaf text. remark-gfm (no remark-breaks) collapses a bare "\n" to a
// space, producing run-on text — so emit a GFM hard break ("  \n") to preserve
// each line. Headings cannot span lines: collapse their newlines to a space.
const heading = (children: PlateNode[] | undefined): string =>
  inlineText(children).replace(/\n/g, ' ');
const paragraph = (children: PlateNode[] | undefined): string =>
  inlineText(children).replace(/\n/g, '  \n');

const serializeBlock = (node: PlateNode): string => {
  const type = node.type ?? 'p';
  switch (type) {
    case 'h1': return `# ${heading(node.children)}`;
    case 'h2': return `## ${heading(node.children)}`;
    case 'h3': return `### ${heading(node.children)}`;
    case 'h4': return `#### ${heading(node.children)}`;
    case 'h5': return `##### ${heading(node.children)}`;
    case 'h6': return `###### ${heading(node.children)}`;
    case 'blockquote': return `> ${inlineText(node.children).replace(/\n/g, '  \n> ')}`;
    case 'hr': return '---';
    case 'img': {
      const url = node.url ?? '';
      if (!url) return '';
      return `![${(node.alt ?? '').replace(/\]/g, '')}](${url})`;
    }
    case 'code_block':
      return '```\n' + inlineText(node.children) + '\n```';
    case 'ul':
      return (node.children ?? [])
        .map((li) => `- ${liText(li)}`)
        .join('\n');
    case 'ol':
      return (node.children ?? [])
        .map((li, i) => `${i + 1}. ${liText(li)}`)
        .join('\n');
    case 'table':
      return serializeTable(node);
    case 'p':
    default:
      return paragraph(node.children);
  }
};

const serializeNodes = (nodes: PlateNode[]): string => {
  const out: string[] = [];
  let i = 0;
  while (i < nodes.length) {
    const node = nodes[i];
    const style = node.listStyleType;
    if (node.type === 'p' && style) {
      const ordered = isOrdered(style);
      const items: string[] = [];
      let n = 1;
      while (i < nodes.length) {
        const cur = nodes[i];
        if (cur.type !== 'p' || !cur.listStyleType) break;
        if (isOrdered(cur.listStyleType) !== ordered) break;
        const line = inlineText(cur.children).replace(/\n/g, ' ');
        items.push(ordered ? `${n}. ${line}` : `- ${line}`);
        n += 1;
        i += 1;
      }
      out.push(items.join('\n'));
      continue;
    }
    out.push(serializeBlock(node));
    i += 1;
  }
  return out.filter((s) => s.trim().length > 0).join('\n\n');
};

export const platePagesToMarkdown = (pages: PageLike[] | null | undefined): string => {
  if (!Array.isArray(pages) || pages.length === 0) return '';
  return pages
    .map((p) => (Array.isArray(p?.plateValue) ? serializeNodes(p.plateValue as PlateNode[]) : ''))
    .filter((s) => s.trim().length > 0)
    .join('\n\n');
};

// A content page carries at least one section heading (h2–h6). The branded
// editor prepends a cover page (logo, title, org, date) and a Table of Contents
// page — both presentation chrome that contain NO section heading. The generation
// page is a plain content renderer, so its canonical markdown should exclude that
// chrome while still reflecting every edit to the actual content.
const hasSectionHeading = (nodes: PlateNode[]): boolean =>
  nodes.some((n) => typeof n.type === 'string' && /^h[2-6]$/.test(n.type));

/**
 * Serializes ONLY the policy content pages to markdown — excludes the cover page
 * and the Table of Contents page, and strips logo images. Used to refresh
 * `policy_markdown_url` (the generation page display) on every editor save, so the
 * generation page shows section content only. The editor's own state
 * (`editor_content_url`) keeps the cover + TOC for editing/PDF export.
 *
 * Falls back to serializing all pages when no page contains a section heading,
 * so an unusual structure never produces empty markdown.
 */
export const contentPagesToMarkdown = (pages: PageLike[] | null | undefined): string => {
  if (!Array.isArray(pages) || pages.length === 0) return '';
  const contentPages = pages.filter(
    (p) => Array.isArray(p?.plateValue) && hasSectionHeading(p.plateValue as PlateNode[]),
  );
  const source = contentPages.length > 0 ? contentPages : pages;
  const stripped: PageLike[] = source.map((p) => ({
    plateValue: Array.isArray(p?.plateValue)
      ? (p.plateValue as PlateNode[]).filter((n) => n.type !== 'img')
      : [],
  }));
  return platePagesToMarkdown(stripped);
};
